import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

export const AUTH_COOKIE = "Authorization";

const USER_AUTH_PREFIXES = ["CO::", "GO::", "LI::"];

function getCookies() {
	return new Cookies();
}

export function getAuthorizationToken() {
	return getCookies().get(AUTH_COOKIE) || null;
}

export function hasUserAuthPrefix(token = getAuthorizationToken()) {
	return USER_AUTH_PREFIXES.some((prefix) => token?.startsWith(prefix));
}

export function stripAuthorizationPrefix(token = getAuthorizationToken()) {
	if (!token) return null;

	for (const prefix of USER_AUTH_PREFIXES) {
		if (token.startsWith(prefix)) {
			return token.slice(prefix.length);
		}
	}

	return token;
}

export function getAuthorizationTokenExpiration(token = getAuthorizationToken()) {
	const decodedToken = decodeToken(stripAuthorizationPrefix(token));
	if (!decodedToken?.exp) {
		return null;
	}

	return new Date(decodedToken.exp * 1000);
}

export function decodeClaimAuthorizationToken(token = getAuthorizationToken()) {
	if (!token) {
		return null;
	}

	try {
		const decodedToken = decodeToken(token);
		const reviewToken = decodedToken?.data?.review_token;
		const claimPayload = typeof reviewToken === "string" ? JSON.parse(reviewToken) : null;

		if (!claimPayload?.companyId) {
			return null;
		}

		return {
			...claimPayload,
			expiresAt: getAuthorizationTokenExpiration(token)?.toISOString() || null,
		};
	} catch {
		return null;
	}
}

export function isClaimAuthorizationToken(token = getAuthorizationToken()) {
	return Boolean(decodeClaimAuthorizationToken(token)?.companyId);
}

export function isUserAuthorizationToken(token = getAuthorizationToken()) {
	return Boolean(token) && hasUserAuthPrefix(token) && !isClaimAuthorizationToken(token);
}

export function isUserAuthorizationExpired(token = getAuthorizationToken()) {
	if (!isUserAuthorizationToken(token)) {
		return true;
	}

	if (token.startsWith("LI::")) {
		return false;
	}

	try {
		const decodedToken = decodeToken(stripAuthorizationPrefix(token));
		return decodedToken?.exp < Date.now() / 1000;
	} catch {
		return true;
	}
}

export function clearAuthorizationToken() {
	getCookies().remove(AUTH_COOKIE, {
		path: "/",
	});
}

export function setAuthorizationToken(token, expiresAt) {
	if (!token) {
		return;
	}

	const expirationDate = expiresAt ? new Date(expiresAt) : getAuthorizationTokenExpiration(token);

	clearAuthorizationToken();
	getCookies().set(AUTH_COOKIE, token, {
		path: "/",
		...(expirationDate ? { expires: expirationDate } : {}),
	});
}
